import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Le token est déjà géré par l'intercepteur dans api.js

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    toast.info('Déconnexion réussie');
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      console.error('Erreur récupération utilisateur:', error);
      if (error.response?.status === 401 || error.isNetworkError) {
        logout();
      } else {
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      
      toast.success(`Bienvenue ${userData.name} !`);
      return { success: true };
    } catch (error) {
      let message = 'Erreur de connexion';
      
      if (error.response) {
        // Le serveur a répondu avec un code d'erreur
        message = error.response.data?.message || `Erreur ${error.response.status}`;
      } else if (error.isNetworkError || !error.response) {
        // La requête a été faite mais aucune réponse n'a été reçue
        message = error.message || 'Impossible de se connecter au serveur. Vérifiez que le serveur est démarré sur le port 5000.';
        console.error('❌ Erreur réseau:', error);
        console.error('💡 Vérifiez que le serveur est démarré: npm run dev');
      } else {
        // Une erreur s'est produite lors de la configuration de la requête
        message = error.message || 'Erreur de connexion';
      }
      
      toast.error(message);
      return { success: false, error: message };
    }
  };


  const changePassword = async (oldPassword, newPassword) => {
    try {
      await authAPI.changePassword(oldPassword, newPassword);
      toast.success('Mot de passe modifié avec succès');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Erreur lors du changement de mot de passe';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    changePassword,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isFormateur: user?.role === 'formateur' || user?.role === 'admin',
    canRead: true // Tous les utilisateurs connectés peuvent lire
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

